"use client";

import { useEffect, useState } from "react";

type DayItem = { id: string; label: string };

// Sticky "Day 1 / Day 2 / …" pill bar for the itinerary. The day content is
// injected via dangerouslySetInnerHTML into #itineraryOutput, so this reads the
// DOM after mount, tags each <section> with an anchor id, and tracks the active
// day with an IntersectionObserver. Renders nothing for single-day trips.
export default function ItineraryDayNav() {
  const [days, setDays] = useState<DayItem[]>([]);
  const [active, setActive] = useState<string>("");

  useEffect(() => {
    const output = document.getElementById("itineraryOutput");
    if (!output) return;

    const sections = Array.from(output.querySelectorAll("section"));
    if (sections.length < 2) return;

    const items: DayItem[] = sections.map((section, i) => {
      const id = `day-${i + 1}`;
      section.id = id;
      return { id, label: `Day ${i + 1}` };
    });
    setDays(items);
    setActive(items[0].id);

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActive((entry.target as HTMLElement).id);
          }
        });
      },
      { rootMargin: "-45% 0px -50% 0px", threshold: 0 }
    );
    sections.forEach((section) => observer.observe(section));

    return () => observer.disconnect();
  }, []);

  if (days.length < 2) return null;

  const handleClick = (id: string) => {
    const el = document.getElementById(id);
    if (!el) return;
    el.scrollIntoView({ behavior: "smooth", block: "start" });
    setActive(id);
  };

  return (
    <nav className="sticky top-16 z-40 mb-4 print:hidden" aria-label="Jump to day">
      <div className="flex gap-2 overflow-x-auto rounded-full border border-gray-200 bg-white/90 p-1.5 shadow-sm backdrop-blur-md">
        {days.map((day) => (
          <button
            key={day.id}
            onClick={() => handleClick(day.id)}
            aria-current={active === day.id ? "true" : undefined}
            className={`whitespace-nowrap rounded-full px-4 py-2 text-sm font-semibold transition-all duration-200 hover:cursor-pointer ${
              active === day.id
                ? "bg-gradient-to-r from-orange-500 to-amber-400 text-white shadow-md"
                : "text-gray-600 hover:bg-orange-50 hover:text-orange-600"
            }`}
          >
            {day.label}
          </button>
        ))}
      </div>
    </nav>
  );
}
